import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
import { formatApprovalCountdownTime } from "@/helpers/waves/approve-wave.helpers";
import { useApprovalDropStatus } from "@/hooks/waves/useApprovalDropStatus";
interface WaveLeaderboardDropRatersProps {
  readonly drop: ExtendedDrop;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
}
export const WaveLeaderboardDropRaters: React.FC<WaveLeaderboardDropRatersProps> = t0 => {
  const $ = _c(45);
  const {
    drop,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t1
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const votersCountLabel = drop.raters_count === 1 ? "voter" : "voters";
  const totalVote = drop.rating;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalVoteClass = totalVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const userVoteClass = userVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const displayWinningThreshold = typeof winningThreshold === "number" && winningThreshold > 0 ? winningThreshold : null;
  const hasWinningThreshold = displayWinningThreshold !== null;
  let t2;
  if ($[0] !== displayWinningThreshold || $[1] !== drop || $[2] !== isVotingClosed || $[3] !== winningThresholdMinDurationMs) {
    t2 = {
      drop,
      isClosed: isVotingClosed,
      winningThreshold: displayWinningThreshold,
      winningThresholdMinDurationMs
    };
    $[0] = displayWinningThreshold;
    $[1] = drop;
    $[2] = isVotingClosed;
    $[3] = winningThresholdMinDurationMs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const approvalStatus = useApprovalDropStatus(t2);
  let approvalStatusClass;
  if (approvalStatus.kind === "approved" || approvalStatus.kind === "approving" || approvalStatus.kind === "reached_threshold") {
    approvalStatusClass = "tw-text-emerald-400";
  } else {
    if (approvalStatus.kind === "closed") {
      approvalStatusClass = "tw-text-amber-300";
    }
  }
  let t3;
  bb0: {
    if (!hasWinningThreshold) {
      t3 = null;
      break bb0;
    }
    if (approvalStatus.kind === "approved") {
      t3 = "Approved";
      break bb0;
    }
    if (approvalStatus.kind === "approving") {
      const t4 = approvalStatus.countdownMs ?? 0;
      let t5;
      if ($[5] !== t4) {
        t5 = formatApprovalCountdownTime(t4);
        $[5] = t4;
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t3 = `Approving in ${t5}`;
      break bb0;
    }
    if (approvalStatus.kind === "reached_threshold") {
      t3 = "Reached threshold";
      break bb0;
    }
    if (approvalStatus.kind === "closed") {
      t3 = "Closed";
      break bb0;
    }
    const t4 = approvalStatus.remaining ?? 0;
    let t5;
    if ($[7] !== t4) {
      t5 = formatNumberWithCommas(t4);
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t3 = `Needs ${t5}`;
  }
  const approvalStatusLabel = t3;
  const hasUserVoted = userVote !== 0;
  const t4 = `tw-font-medium ${totalVoteClass}`;
  let t5;
  if ($[9] !== totalVote) {
    t5 = formatNumberWithCommas(totalVote);
    $[9] = totalVote;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== displayWinningThreshold) {
    t7 = displayWinningThreshold !== null && <><span className="tw-font-medium tw-text-iron-500">/</span><span className="tw-font-medium tw-text-iron-50">{formatNumberWithCommas(displayWinningThreshold)}</span></>;
    $[14] = displayWinningThreshold;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== drop.rating || $[17] !== drop.rating_prediction) {
    t8 = <DropVoteProgressing current={drop.rating} projected={drop.rating_prediction} compact={true} />;
    $[16] = drop.rating;
    $[17] = drop.rating_prediction;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t6}{t7}{t8}</div>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== approvalStatusClass || $[24] !== approvalStatusLabel || $[25] !== hasWinningThreshold || $[26] !== votingLabel) {
    t10 = <span className="tw-whitespace-nowrap tw-font-normal tw-text-iron-400">{hasWinningThreshold && approvalStatusLabel !== null ? <span className={approvalStatusClass}>{approvalStatusLabel}</span> : <>{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</>}</span>;
    $[23] = approvalStatusClass;
    $[24] = approvalStatusLabel;
    $[25] = hasWinningThreshold;
    $[26] = votingLabel;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-leading-5">{t9}{t10}</div>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== drop.raters_count) {
    t12 = formatNumberWithCommas(drop.raters_count);
    $[31] = drop.raters_count;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12 || $[34] !== votersCountLabel) {
    t13 = <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-text-sm tw-leading-5"><span className="tw-text-iron-400">{t12} {votersCountLabel}</span></div>;
    $[33] = t12;
    $[34] = votersCountLabel;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== hasUserVoted || $[37] !== userVote || $[38] !== userVoteClass || $[39] !== votingLabel) {
    t14 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-leading-5"><span className="tw-whitespace-nowrap"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}</span><span className={`tw-font-medium ${userVoteClass}`}>{formatNumberWithCommas(userVote)} {votingLabel}</span></span></div>;
    $[36] = hasUserVoted;
    $[37] = userVote;
    $[38] = userVoteClass;
    $[39] = votingLabel;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t11 || $[42] !== t13 || $[43] !== t14) {
    t15 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 sm:tw-justify-end">{t11}{t13}{t14}</div>;
    $[41] = t11;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
};